import type { ErrorPageContent } from "./entries/error-pages";
import { getSynchronousRuntimeErrorContent } from "./entries/runtime-error-fallback";
import type { AppLocale } from "../i18n/constants";

type ErrorPagesModule = {
  getErrorPageContent: (kind: "runtime-error", locale: string | undefined) => ErrorPageContent;
  toErrorPageLocale: (locale: string | undefined) => AppLocale;
};

type SharedChromeModule = {
  getMainLayoutMessages: (locale: AppLocale) => { readonly skipToContent: string };
};

export type RuntimeErrorContentLoaders = {
  loadErrorPages: () => Promise<ErrorPagesModule>;
  loadSharedChrome: () => Promise<SharedChromeModule>;
};

export type LocalizedRuntimeErrorContent = {
  readonly content: ErrorPageContent;
  readonly skipLabel: string;
};

const DEFAULT_LOADERS: RuntimeErrorContentLoaders = {
  loadErrorPages: () => import("./entries/error-pages"),
  loadSharedChrome: () => import("./entries/shared-chrome"),
};

/**
 * Load the locale-heavy error dictionaries without putting them in every
 * public route's initial client bundle. A rejected chunk returns the same
 * synchronous locale-specific fallback used for the first boundary render.
 */
export async function loadLocalizedRuntimeErrorContent(
  locale: string | undefined,
  loaders: RuntimeErrorContentLoaders = DEFAULT_LOADERS,
): Promise<LocalizedRuntimeErrorContent> {
  try {
    const [errorPages, sharedChrome] = await Promise.all([
      loaders.loadErrorPages(),
      loaders.loadSharedChrome(),
    ]);
    const resolvedLocale = errorPages.toErrorPageLocale(locale);

    return {
      content: errorPages.getErrorPageContent("runtime-error", resolvedLocale),
      skipLabel: sharedChrome.getMainLayoutMessages(resolvedLocale).skipToContent,
    };
  } catch {
    return getSynchronousRuntimeErrorContent(locale);
  }
}
